import React, { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { socialAuthService, SocialAuthResponse } from '../services/socialAuthService';
import '../styles/pages/_social-callback.scss';

const SocialCallback: React.FC = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState(false);

  useEffect(() => {
    const processCallback = async () => {
      try {
        setLoading(true);
        
        // Obter parâmetros da URL
        const code = searchParams.get('code');
        const provider = searchParams.get('provider') || 'google';
        const errorParam = searchParams.get('error');
        
        // Verificar se houve erro na autenticação
        if (errorParam) {
          setError(`Erro na autenticação: ${errorParam}`);
          setLoading(false);
          return;
        }
        
        // Verificar se temos o código de autorização
        if (!code) {
          setError('Código de autorização não encontrado');
          setLoading(false);
          return;
        }
        
        // Processar callback no backend
        const response: SocialAuthResponse = await socialAuthService.processCallback(
          provider as 'google' | 'facebook',
          code,
          'login' // Default para login, mas o backend decide baseado na existência do usuário
        );
        
        if (response.success) {
          setSuccess(true);
          
          // Salvar tokens se fornecidos
          if (response.tokens) {
            localStorage.setItem('access_token', response.tokens.access);
            localStorage.setItem('refresh_token', response.tokens.refresh);
          }
          
          // Aguardar um pouco para mostrar mensagem de sucesso
          setTimeout(() => {
            // Redirecionar baseado no tipo de fluxo
            if (response.flow_type === 'signup') {
              // Novo usuário - ir para completar perfil
              navigate('/inicio/completar-perfil');
            } else {
              // Usuário existente - ir direto para área do membro
              navigate('/membro/home');
            }
          }, 2000);
        } else {
          setError(response.message || 'Erro ao processar autenticação');
        }
        
      } catch (err: any) {
        console.error('Erro no callback social:', err);
        setError(err.message || 'Erro inesperado durante a autenticação');
      } finally {
        setLoading(false);
      }
    };

    processCallback();
  }, [searchParams, navigate]);

  const handleRetry = () => {
    // Limpar estado e tentar novamente
    setError(null);
    setLoading(true);
    window.location.reload();
  };

  const handleGoHome = () => {
    navigate('/');
  };

  if (loading) {
    return (
      <div className="social-callback">
        <div className="callback-container">
          <div className="loading-spinner"></div>
          <h2>Processando autenticação...</h2>
          <p>Aguarde enquanto processamos sua autenticação social.</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="social-callback">
        <div className="callback-container error">
          <div className="error-icon">❌</div>
          <h2>Erro na autenticação</h2>
          <p className="error-message">{error}</p>
          <div className="action-buttons">
            <button onClick={handleRetry} className="btn btn-primary">
              Tentar novamente
            </button>
            <button onClick={handleGoHome} className="btn btn-secondary">
              Ir para o início
            </button>
          </div>
        </div>
      </div>
    );
  }

  if (success) {
    return (
      <div className="social-callback">
        <div className="callback-container success">
          <div className="success-icon">✅</div>
          <h2>Autenticação realizada com sucesso!</h2>
          <p>Você será redirecionado em alguns segundos...</p>
          <div className="loading-dots">
            <span></span>
            <span></span>
            <span></span>
          </div>
        </div>
      </div>
    );
  }

  return null;
};

export default SocialCallback;
